import { useEffect, useState } from "react";
import { ResponsiveContainer, ComposedChart, Line, Bar, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { Sparkles, Eye, Droplets, Loader2 } from "lucide-react";

const SPIA = {
  pulito: { c: "#10B981", I: Sparkles },
  osservare: { c: "#F59E0B", I: Eye },
  pulizia: { c: "#EF4444", I: Droplets },
};
const mese3 = (s) => new Date(`${s}T12:00:00`).toLocaleDateString("it-IT", { month: "short" });
const dataBreve = (s) => new Date(`${s}T12:00:00`).toLocaleDateString("it-IT", { day: "numeric", month: "short" });

// Slot Vista Schermo: stato di pulizia pannelli (oggi e mese), grafico riassuntivo e stima delle perdite
export function SchermoPulizia({ imp, src }) {
  const d = imp.pulizia || {};
  const [res, setRes] = useState(null);
  useEffect(() => { src.puliziaStorico?.().then(setRes).catch(() => setRes({ punti: [] })); }, [src]);
  const pts = res ? res.punti || [] : null;
  const daily = res?.granularita === "giorno";
  const xKey = daily ? "giorno" : "settimana";
  const lineKey = daily ? "pulizia_sm" : "pulizia";
  const validi = (pts || []).filter((p) => p[lineKey] != null);
  const oggiPct = validi.length ? validi[validi.length - 1][lineKey] : null;
  const recent = daily ? validi.slice(-30) : validi.slice(-4);
  const mesePct = recent.length ? Math.round(recent.reduce((a, p) => a + p[lineKey], 0) / recent.length) : oggiPct;
  // Grafico: solo l'anno corrente a partire da gennaio
  const yStart = `${new Date().getFullYear()}-01-01`;
  const chartPts = (pts || []).filter((p) => p[xKey] >= yStart);
  const chartValidi = chartPts.filter((p) => p[lineKey] != null);
  const persoPct = d.perdita_pct;
  const kwhMese = d.perdita_kwh_mese;
  const kwhAnno = kwhMese != null ? Math.round(kwhMese * 12) : null;
  const s = SPIA[d.spia] || null;
  const pioggiaTxt = d.ultima_pioggia ? `ultima pioggia utile ${dataBreve(d.ultima_pioggia)}${d.giorni_da_pioggia != null ? ` · ${d.giorni_da_pioggia} gg fa` : ""}` : null;

  const descr = (() => {
    if (oggiPct == null && persoPct == null) return "Stiamo ancora raccogliendo giornate di sole e piogge per stimare lo stato di pulizia dei pannelli.";
    if (persoPct == null || persoPct < 2) return "I pannelli sono puliti: la perdita di produzione è trascurabile e per ora non serve nessun intervento.";
    let t = `Con questo livello di sporco stai perdendo circa il ${String(persoPct).replace(".", ",")}% di produzione`;
    if (kwhMese) t += ` (~${kwhMese} kWh questo mese). Se la situazione resta così, sull'anno la stima è di circa ${kwhAnno} kWh in meno`;
    return `${t}.`;
  })();

  return (
    <section className="flex min-h-0 flex-1 flex-col rounded-3xl bg-white p-4 shadow-sm ring-1 ring-slate-100" data-testid="schermo-pulizia">
      <div className="mb-3 flex items-center gap-2">
        {s && <span className="flex h-8 w-8 items-center justify-center rounded-full" style={{ background: `${s.c}22`, color: s.c }} data-testid={`schermo-pulizia-spia-${d.spia}`}><s.I className="h-4 w-4" /></span>}
        <h3 className="text-sm font-bold uppercase tracking-widest text-slate-600" data-testid="schermo-pulizia-titolo">Algoritmo VESTA · Pulizia pannelli</h3>
        {d.spia === "pulizia" && (
          <span className="ml-auto flex items-center gap-1.5 rounded-full bg-red-50 px-3 py-1 text-xs font-bold text-red-600 ring-1 ring-red-100" data-testid="schermo-pulizia-badge">
            <Droplets className="h-3.5 w-3.5" />Pulizia consigliata{pioggiaTxt ? ` · ${pioggiaTxt}` : ""}
          </span>
        )}
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div className="rounded-2xl bg-slate-50 p-3 text-center" data-testid="schermo-pulizia-oggi">
          <b className="block text-3xl font-extrabold text-slate-800">{oggiPct != null ? `${oggiPct}%` : "—"}</b>
          <span className="text-[11px] font-semibold text-slate-500">Pulizia oggi</span>
        </div>
        <div className="rounded-2xl bg-slate-50 p-3 text-center" data-testid="schermo-pulizia-mese">
          <b className="block text-3xl font-extrabold text-slate-800">{mesePct != null ? `${mesePct}%` : "—"}</b>
          <span className="text-[11px] font-semibold text-slate-500">Pulizia del mese</span>
        </div>
      </div>
      <div className="mt-3 min-h-0 flex-1" data-testid="schermo-pulizia-grafico">
        {!pts ? <div className="flex h-full items-center justify-center"><Loader2 className="h-5 w-5 animate-spin text-sky-500" /></div>
          : chartValidi.length < 4 ? <p className="flex h-full items-center justify-center text-center text-xs text-slate-400">In attesa di giornate di sole sufficienti…</p>
          : <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={chartPts} margin={{ top: 6, right: 4, left: -20, bottom: 0 }}>
                <CartesianGrid stroke="#F1F5F9" vertical={false} />
                <XAxis dataKey={xKey} tickFormatter={mese3} interval={daily ? 30 : 6} tick={{ fontSize: 10, fill: "#94A3B8" }} axisLine={false} tickLine={false} />
                <YAxis yAxisId="pct" domain={[(m) => Math.min(60, Math.floor(m / 10) * 10), 100]} tick={{ fontSize: 10, fill: "#94A3B8" }} axisLine={false} tickLine={false} unit="%" />
                <YAxis yAxisId="mm" orientation="right" domain={[0, (m) => Math.max(10, Math.ceil(m / 5) * 5)]} tick={{ fontSize: 10, fill: "#38BDF8" }} axisLine={false} tickLine={false} width={30} unit=" mm" />
                <Tooltip formatter={(v, name) => (name === "pioggia_mm" ? [`${v} mm`, "Pioggia"] : [`${v}%`, "Pulizia"])} labelFormatter={(x) => (daily ? new Date(`${x}T12:00:00`).toLocaleDateString("it-IT", { day: "numeric", month: "long" }) : mese3(x))} />
                <Bar yAxisId="mm" dataKey="pioggia_mm" fill="#BAE6FD" radius={[2, 2, 0, 0]} maxBarSize={daily ? 4 : 14} />
                <Line yAxisId="pct" dataKey={lineKey} stroke="#10B981" strokeWidth={2.5} dot={false} connectNulls />
              </ComposedChart>
            </ResponsiveContainer>}
      </div>
      <p className="mt-2 text-sm leading-snug text-slate-600" data-testid="schermo-pulizia-descr">{descr}</p>
    </section>
  );
}
